import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import english from '../../assets/lang/english';
import { RootState } from '../../redux/rootState';
import DataTable from '../../Common/DataTable';
import ComponentDeleteModal from '../../Common/DeleteModal/ComponentDeleteModal';
import { setLoader } from '../../redux/loaderSlice';
import ComponentPageHeader from '../../Common/PageHeader/ComponentPageHeader';
import { exportToExcelData } from '../../Common/PageHeader/helper';
import { JobOrderData } from './utils/types';
import {
  deleteJobOrderStart, getJobOrderData, JobOrderMoveWorkspaceStart, setCheckedIds,
} from './redux/sliceJobOrder';
import {
  resetPagination, setPageNumber, setPageSize, setSearchParam,
  setSortColumn,
  setSortDirection,
} from '../taiReportingSystem/redux/sliceReportWorkspace';

export default function ScreenJobOrder() {
  const Navigate = useNavigate();
  // const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [searchQuery, setSearchQuery] = useState('');
  const {
    pageNumber, pageSize, searchParam, sortColumn, sortDirection,
  } = useSelector((state: RootState) => state.reportWorkspaceReducer);
  const { historyStack } = useSelector((state:RootState) => state.masterDataReducer);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJobOrderId, setSelectedJobOrderId] = useState('');
  const [data, setData] = useState<JobOrderData[]>([]);

  const dispatch = useDispatch();
  const { jobOrderData, isLoading, checkedIds } = useSelector((state: RootState) => state.jobOrderReducer);

  const handleData = () => {
    const params = {
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
      sortCol: sortColumn,
      sortDir: sortDirection,
    };
    dispatch(getJobOrderData(params));
  };

  useEffect(() => {
    handleData();
  }, [pageNumber, pageSize, searchParam, sortColumn, sortDirection]);

  useEffect(() => () => {
    dispatch(setCheckedIds([]));
  }, []);

  const handleDelete = (id: string) => {
    setShowDeleteModal(true);
    setSelectedJobOrderId(id);
  };

  const handleDeleteModal = () => {
    setShowDeleteModal(false);
    const payload = {
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
      sortCol: sortColumn,
      sortDir: sortDirection,
      id: selectedJobOrderId,
    };
    dispatch(deleteJobOrderStart(payload));
    dispatch(setCheckedIds([]));
  };

  const columns = useMemo(
    () => [
      { key: 'sn', label: '' },
      { key: 'action', label: 'Action' },
      // { key: 'ReadyToSend', label: 'Ready To Send' },
      { key: 'IsCompleted', label: 'Job Completed?' },
      { key: 'JobOrderNumber_infoTool', label: 'Job Order #' },
      { key: 'JobDate', label: 'Job Date' },
      { key: 'Version_extra', label: 'Version Used for Sync' },
      { key: 'ToolTipVesselRedirect', label: 'Vessels' },
      { key: 'CustomerName', label: 'Customer' },
      { key: 'Model', label: 'Model' },
      { key: 'SerialNumber', label: 'Serial #' },
      { key: 'SiteName', label: 'Site' },
      { key: 'SiteCity', label: 'Site City' },
      { key: 'SiteAddress', label: 'Site Address' },
      { key: 'PoNumber', label: 'PO Number' },
      // { key: 'SiteAddressOptional', label: 'Site Address (Optional)' },
    ],
    [],
  );

  const apiData: JobOrderData[] = useMemo(() => jobOrderData?.Data?.map((item, index) => {
    const transformedDataItem = {
      ReadyToSend: item?.IsReadyToSend,
      IsCompleted: item?.IsCompleted,
      Date: item?.Date,
      JobOrderNumber_infoTool: item?.JobOrderNumber,
      Version_extra: item?.Version,
      CustomerName: item?.CustomerName,
      Model: item?.Model,
      SerialNumber: item?.SerialNumber,
      SiteName: item?.SiteName,
      SiteCity: (item?.SiteCity)?.toUpperCase(),
      action: item?.IsCompleted ? 'edit&delete&sendMail' : 'edit&delete',
      sn: index,
      id: item?.JobOrderNumber,
      idUrl: `${item?.JobOrderNumber}/${item?.CustomerId}`,
      isChecked: checkedIds.includes(`${item?.JobOrderNumber}`),
      SiteAddress: item?.SiteAddress,
      SiteAddressOptional: item?.SiteAddressOptional,
      JobDate: item?.JobDate,
      ToolTipVesselRedirect: item?.Vessels,
      PoNumber: item?.PoNumber,
      isJobCopied: item?.IsJobCopied,
      infoText: item?.ParentJob || '',
    };
    return transformedDataItem;
  }), [jobOrderData, checkedIds]);

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  const handleEdit = (id: string) => {
    Navigate(`${id}`);
  };

  const handleCheckBoxWithKey = (id: string) => {
    const jobIds: string[] = [...checkedIds];
    const updatedData = data?.map((element: JobOrderData) => {
      if (element?.id === id) {
        if (!element.isChecked) {
          jobIds.push(element.id);
        } else {
          const ind = jobIds.findIndex((ele) => ele === id);
          jobIds.splice(ind, 1);
        }
        return {
          ...element,
          isChecked: !element?.isChecked,
        };
      }
      return element;
    });

    setData(updatedData);
    dispatch(setCheckedIds(jobIds));
  };

  useEffect(() => {
    if (jobOrderData) {
      setData(apiData);
    }
  }, [jobOrderData, apiData]);

  const excelData = useMemo(() => jobOrderData?.Data?.map((item) => {
    const transformedDataItem = {
      // ReadyToSend: item?.IsReadyToSend ? 'Yes' : 'No',
      Is_Job_Completed: item?.IsCompleted ? 'Yes' : 'No',
      Date: item?.Date,
      Job_Order: item?.JobOrderNumber,
      Customer: item?.CustomerName,
      Model: item?.Model,
      Serial_Number: item?.SerialNumber,
      Site_Name: item.SiteName,
      Site_City: (item?.SiteCity)?.toUpperCase(),
      Site_Address: item?.SiteAddress,
      // Site_Address_Optional: item?.SiteAddressOptional,
      Job_Date: item?.JobDate,
      Vessels: item?.Vessels?.join(', '),
      PO_Number: item?.PoNumber,
    };
    return transformedDataItem;
  }), [jobOrderData]);

  const handleButtonClick = () => {
    dispatch(JobOrderMoveWorkspaceStart(checkedIds));
  };

  const downloadExcel = () => {
    exportToExcelData(
      {
        data: excelData,
      },
      'Job_Order_list',
    );
  };

  useEffect(() => {
    if (historyStack) {
      const finderString = '/job-order/';
      const last = historyStack[historyStack.length - 1];
      const sLast = historyStack[historyStack.length - 2];
      if (!(
        last?.includes(finderString)
        || (sLast?.includes(finderString) && last === '/job-order')
      )) {
        dispatch(resetPagination());
      }
    }
  }, [historyStack]);

  const handleSendEmail = (id: string) => {
    const selected = apiData?.find((item) => item?.id === id);
    Navigate(`${selected?.idUrl}`);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <ComponentPageHeader subHeading={String(english.Job_Order)} downloadExcel={downloadExcel} />
        </Grid>
        <Grid item xs={12} sm={12} md={12} className="dataTableCustomMargin">
          <DataTable<JobOrderData>
            data={data}
            columns={columns}
            totalRecords={jobOrderData?.TotalRecords}
            checkBoxColumns={['IsCompleted']}
            // checkBoxColumns={['ReadyToSend']}
            page={pageNumber}
            setPage={(e) => { dispatch(setPageNumber(e)); }}
            rowsPerPage={pageSize}
            setRowsPerPage={(e) => { dispatch(setPageSize(e)); }}
            searchQuery={searchParam}
            setSearchQuery={(e) => { dispatch(setSearchParam(e)); dispatch(setPageNumber(1)); }}
            sortCol={sortColumn}
            setSortCol={(e) => { dispatch(setSortColumn(e)); dispatch(setPageNumber(1)); }}
            sortDir={sortDirection}
            setSortDir={(e) => { dispatch(setSortDirection(e)); dispatch(setPageNumber(1)); }}
            handleData={handleData}
            checkBoxWithKey={['sn']}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            handleOilReportEmailModal={handleSendEmail}
            handleCheckBoxWithKey={handleCheckBoxWithKey}
            buttonText="Move to Workspace"
            handleButtonClick={() => handleButtonClick()}
            textButtonDisabled={Boolean(checkedIds.length === 0)}
            handleDataFromComponent
            textButtonDisabledSixth={Boolean(checkedIds.length === 0) || Boolean(checkedIds.length > 1)}
          />
        </Grid>
      </Grid>
      <ComponentDeleteModal
        show={showDeleteModal}
        setOpenFrom={() => setShowDeleteModal(false)}
        handleDelete={handleDeleteModal}
      />
    </>
  );
}
